import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Header } from "../../../components/Header/Header";
import FilterModalForFolder from "../../Contract/FilterModalForFolder";
import "./KbFolderPage.css";
import UploadModal from "./UploadFolderModal";
import api from "../../chat/api";
import { getOrFetchSubscriptionStatus } from "../../../utils/subscriptionStatus";
import RenameModal from "./RenameModal";
import DeleteModal from "./DeleteModal";
import hotToast from "react-hot-toast";

// The three states shown as grouped sections inside "Previous Proposals" —
// the one folder that holds every captured proposal regardless of outcome
// (see rfp.services.kb_folders on the backend). "Won"/"Work Completed" are
// NOT separate folders; moving a document between groups is a tier change,
// never a folder move. Lower number sorts first; anything not in this map (a
// plain upload with no tier, or a 'claimed'/'scraped' straggler) reads as
// "Submitted" since that tier's own meaning is "outcome unknown".
const PROPOSAL_TIERS = [
  { key: "past_proposal", label: "Submitted" },
  { key: "won", label: "Won" },
  { key: "delivered", label: "Work Completed" },
];
const PROPOSAL_TIER_ORDER = { past_proposal: 0, won: 1, delivered: 2 };
const PROPOSAL_TIER_LABEL = { past_proposal: "Submitted", won: "Won", delivered: "Work Completed" };
// `overrides` (doc id -> tier key) lets a just-changed tier move a document
// into its new group immediately, without waiting on a refetch.
const effectiveProposalTier = (file, overrides) => {
  if (overrides && file.id in overrides) return overrides[file.id];
  return file.evidence_tier;
};
const proposalTierRank = (file, overrides) =>
  PROPOSAL_TIER_ORDER[effectiveProposalTier(file, overrides)] ?? 0;
const proposalTierLabel = (file, overrides) =>
  PROPOSAL_TIER_LABEL[effectiveProposalTier(file, overrides)] || "Submitted";

const KbFolderPage = () => {
  const location = useLocation();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const { folderName } = useParams();
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const fileInputRef = useRef(null);
  const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false);
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [renameFolderInput, setRenameFolderInput] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [activeActionMenuFolder, setActiveActionMenuFolder] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [clickedAction, setClickedAction] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    folder: false,
    normalDocument: false,
    contract: false,
    invoice: false,
  });
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Optimistic overrides for a doc's proposal tier, keyed by doc id -> tier
  // key, once this session has changed it, so the group it sits in updates
  // immediately without waiting on a refetch. Absent = trust
  // file.evidence_tier from the server.
  const [tierOverrides, setTierOverrides] = useState({});
  const pathSegments = window.location.pathname.split("/");
  const folderId = pathSegments[pathSegments.length - 1];
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isPaidYearlyPlan, setIsPaidYearlyPlan] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [isRenamingDoc, setIsRenamingDoc] = useState(false);
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const actionMenuRef = useRef(null);
  const totalPages = Math.ceil((files.documents?.length || 0) / itemsPerPage);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const showPopup = (type, message) => {
    alert(`${type}: ${message}`);
  };

  const toggleFolderActionMenu = (folder) => {
    setActiveActionMenuFolder((prevFolder) =>
      prevFolder === folder ? null : folder
    );
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const [filters, setFilters] = useState({
    folder: true,
    normalDocument: true,
    contract: false,
    invoices: false,
  });

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsFilterModalOpen(false);
  };

  useEffect(() => {
    if (isPopupVisible) {
      setIsActionMenuOpen(null);
    }
  }, [isPopupVisible]);

  const handleSortByDate = () => {
    setSortConfig((prev) => ({
      key: "date",
      direction:
        prev.key === "date" && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSortBySize = () => {
    setSortConfig((prev) => ({
      key: "size",
      direction:
        prev.key === "size" && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleCheckboxChange = (fileId) => {
    setSelectedFiles((prevSelected) => {
      const updatedSelected = prevSelected.includes(fileId)
        ? prevSelected.filter((id) => id !== fileId)
        : [...prevSelected, fileId];

      setShowBulkActions(updatedSelected.length > 0);

      return updatedSelected;
    });
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const handleSelectAll = () => {
    if (!files.documents || files.documents.length === 0) {
      setSelectedFiles([]);
      setShowBulkActions(false);
      return;
    }

    if (selectedFiles.length === files.documents.length) {
      setSelectedFiles([]);
      setShowBulkActions(false);
    } else {
      const allFileIds = files.documents.map((file) => file.id);
      setSelectedFiles(allFileIds);
      setShowBulkActions(true);
    }
  };

  const convertTimeAgoToSeconds = (timeAgo) => {
    const [value, unit] = timeAgo.split(" ");
    const number = parseInt(value);

    if (isNaN(number)) return Infinity;

    switch (unit.toLowerCase()) {
      case "second":
      case "seconds":
        return number;
      case "minute":
      case "minutes":
        return number * 60;
      case "hour":
      case "hours":
        return number * 3600;
      case "day":
      case "days":
        return number * 86400;
      default:
        return Infinity;
    }
  };

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const response = await getOrFetchSubscriptionStatus(api);

        if (response.status === 200) {
          const { subscription_type, plan_type } = response.data;
          const isYearly =
            subscription_type === "paid_subs" && plan_type === "yearly";
          setIsPaidYearlyPlan(isYearly);
          setSubscriptionChecked(true);
        }
      } catch (error) {
        console.error("Failed to fetch subscription status", error);
      }
    };

    const token = localStorage.getItem("token");
    if (token) {
      fetchSubscriptionStatus();
    }
  }, []);

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;

    setFilesToUpload(selected);
    setIsModalOpen(true);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const filteredDocuments = files.documents
    ? [...files.documents]
        .filter((file) =>
          file.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
          // Won sits above Submitted regardless of the chosen column sort —
          // the column sort still decides order WITHIN each group.
          if (folders.folder_name === "Previous Proposals") {
            const tierDiff = proposalTierRank(a, tierOverrides) - proposalTierRank(b, tierOverrides);
            if (tierDiff !== 0) return tierDiff;
          }

          if (!sortConfig.key) return 0;

          let aVal = a[sortConfig.key];
          let bVal = b[sortConfig.key];

          if (sortConfig.key === "date") {
            aVal = convertTimeAgoToSeconds(a.time_ago || "");
            bVal = convertTimeAgoToSeconds(b.time_ago || "");
          }

          if (sortConfig.key === "size") {
            const parseSize = (sizeStr) => {
              if (!sizeStr) return 0;
              const [val, unit] = sizeStr.toUpperCase().split(" ");
              const num = parseFloat(val);
              const multiplier =
                unit === "KB"
                  ? 1024
                  : unit === "MB"
                  ? 1024 * 1024
                  : unit === "GB"
                  ? 1024 * 1024 * 1024
                  : 1;
              return num * multiplier;
            };

            aVal = parseSize(a.size);
            bVal = parseSize(b.size);
          }

          if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        })
    : [];

  const clearFilters = async () => {
    try {
      setSelectedFilters({
        normalDocument: false,
        contract: false,
        invoice: false,
        spreadsheet: false,
      });

      const response = await api.get(
        `api/v1/readpdf/files-in-the-folder/${folderId}/?folder_type=knowledge_base`
      );

      if (response.status === 200) {
        const { documents, folders, folder_name } = response.data;

        setFiles({
          documents: documents.map((doc) => ({
            ...doc,
            key: doc.key || "",
            pdf_url: doc.url || "",
          })),
        });

        setFolders({ folder_name: folder_name || "" });

        setIsFilterApplied(false);
      }
    } catch (error) {
      console.error("Error clearing filters:", error);
      setFiles({ documents: [] });
      setFolders({ folder_name: "N/A" });
    }
  };

  const handleRenameFolder = async (newName) => {
    if (!newName) {
      showPopup("Error", "Folder name is required.");
      return;
    }

    setIsRenamingFolder(true);
    try {
      const response = await api.put(
        `api/v1/readpdf/rename-folder/${currentFolder.id}/`,
        { new_name: newName }
      );

      if (response.status === 200) {
        await fetchFiles();
        setIsRenameFolderModalOpen(false);
        showPopup("Success", "Folder renamed successfully.");
      }
    } catch (error) {
      console.error("Error renaming folder:", error);
      showPopup("Error", "Failed to rename folder. Please try again.");
    } finally {
      setIsRenamingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      const response = await api.delete(
        `api/v1/readpdf/delete-folder/${folderId}/`
      );

      if (response.status === 204) {
        setFolders((prevFolders) =>
          prevFolders.filter((folder) => folder.id !== folderId)
        );
        setIsDeleteFolderModalOpen(false);
        showPopup("Success", "Folder deleted successfully.");
      } else {
        showPopup("Error", "Failed to delete folder. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
      showPopup("Error", "Failed to delete folder. Please try again.");
    }
  };

  const toggleFilterModal = () => {
    setIsFilterModalOpen((prev) => !prev);
  };

  const handleFolderActionClick = (action, folder) => {
    setCurrentFolder(folder);
    if (action === "rename") {
      setRenameFolderInput(folder.name);
      setIsRenameFolderModalOpen(true);
    } else if (action === "delete") {
      setIsDeleteFolderModalOpen(true);
    }
  };

  const fetchFiles = async (isSilent = false) => {
    try {
      const response = await api.get(
        `api/v1/readpdf/files-in-the-folder/${folderId}/?folder_type=knowledge_base`
      );

      if (response.status === 200) {
        const updatedDocuments = response.data.documents.map((doc) => ({
          ...doc,
          pdf_url: doc.url || "",
        }));
        setFiles({ ...response.data, documents: updatedDocuments });
        setFolders({ folder_name: response.data.folder_name });
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [folderId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isActionMenuOpen !== null &&
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setIsActionMenuOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isActionMenuOpen]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    const limit = isPaidYearlyPlan ? 10 : 5;

    if (subscriptionChecked && files.documents?.length > limit) {
      setIsPopupVisible(true);
    } else {
      setIsPopupVisible(false);
    }
  }, [files.documents, isPaidYearlyPlan, subscriptionChecked]);

  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFilesToUpload([]);
  };

  const handleBulkExport = async () => {
    if (selectedFiles.length === 0) {
      alert("No files selected for export");
      return;
    }

    try {
      setIsExporting(true);

      const selectedFileObjects = files.documents.filter((file) =>
        selectedFiles.includes(file.id)
      );

      const contractKeys = selectedFileObjects
        .filter((file) => file.type === "contract" || file.type === "invoice")
        .map((file) => file.key);

      if (contractKeys.length === 0) {
        alert(
          "Export is only available for contracts. Please select at least one contract."
        );
        return;
      }

      const response = await api.post(
        `api/v1/readpdf/export-doc/`,
        { contract_keys: contractKeys },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "exported_documents.csv";
      link.click();

      setSelectedFiles([]);
      setShowBulkActions(false);
    } catch (error) {
      console.error(
        "Error exporting files:",
        error.response?.data || error.message
      );
      alert("An error occurred while exporting files. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleUploadSuccess = () => {
    fetchFiles();
  };

  const handleRename = async (newName) => {
    if (!currentFile) return;
    setIsRenamingDoc(true);
    try {
      const response = await api.put(
        `api/v1/readpdf/rename-doc/${currentFile.id}/`,
        { new_name: newName }
      );
      if (response.status === 200) {
        await fetchFiles();
        setIsRenameModalOpen(false);
      }
    } catch (error) {
      console.error("Error renaming document:", error);
    } finally {
      setIsRenamingDoc(false);
    }
  };

  const confirmDelete = async () => {
    let payload;

    if (isBulkDelete) {
      const selectedItems =
        files.documents?.filter((item) => selectedFiles.includes(item.id)) ||
        [];

      const doc_ids = selectedItems.map((item) => item.id);

      payload = {
        doc_ids,
        folder_ids: [],
      };
    } else {
      payload = {
        doc_ids: contractToDelete ? [contractToDelete.id] : [],
        folder_ids: [],
      };
    }

    if (!payload.doc_ids || payload.doc_ids.length === 0) {
      showPopup("Error", "No items selected for deletion.");
      setConfirmDeleteVisible(false);
      return;
    }

    setDeleteInProgress(true);
    setDeleteSuccess(false);

    try {
      console.log("Sending delete request with payload:", payload);

      const res = await api.delete(
        "/api/v1/rfp/knowledge-base/documents/delete/",
        { data: payload }
      );

      console.log("Delete response:", res);

      if (res.status === 200) {
        const documents = res?.data?.results?.documents || [];
        const folders = res?.data?.results?.folders || [];
        const allItems = [...documents, ...folders];
        const deletedIds = allItems
          .filter((item) => item.status === "success")
          .map((item) => item.identifier);
        const failedItems = allItems.filter((item) => item.status === "error");

        console.log("Deleted ids:", deletedIds);

        setDeleteInProgress(false);

        if (failedItems.length > 0) {
          failedItems.forEach((item) => {
            hotToast.error(
              `Failed to delete (ID: ${item.identifier}): ${item.error || "Unknown error"}`
            );
          });
        }

        if (deletedIds.length > 0) {
          fetchFiles(true);
        }

        if (deletedIds.length > 0 || allItems.length === 0) {
          setDeleteSuccess(true);

          setTimeout(() => {
            setConfirmDeleteVisible(false);
            setDeleteSuccess(false);
            setContractToDelete(null);
            setSelectedFiles([]);
            setShowBulkActions(false);
          }, 2000);
        } else {
          setConfirmDeleteVisible(false);
          setContractToDelete(null);
          setSelectedFiles([]);
          setShowBulkActions(false);
        }
      }
    } catch (error) {
      console.error("Error deleting items:", error);
      console.error("Error response:", error.response);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete item(s). Please try again.";
      hotToast.error(errorMessage);

      setSelectedFiles([]);
      setShowBulkActions(false);
      setDeleteInProgress(false);
      setDeleteSuccess(false);
      setConfirmDeleteVisible(false);
      setContractToDelete(null);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedFiles.length === 0) {
      alert("No files selected for deletion");
      return;
    }
    setIsBulkDelete(true);
    setConfirmDeleteVisible(true);
  };

  const handleIndividualDelete = (file) => {
    setIsBulkDelete(false);
    setContractToDelete(file);
    setConfirmDeleteVisible(true);
  };

  // Only meaningful in "Previous Proposals": everywhere else, which of these
  // three states a document is in isn't something a folder move alone can
  // decide (see rfp.services.kb_folders on the backend) — this is the one
  // honest, human-driven way to record it for a document nothing can trace
  // back to a project automatically, or to correct the automatic path.
  const currentProposalTier = (file) => effectiveProposalTier(file, tierOverrides);

  const handleSetProposalTier = async (file, tier) => {
    if (currentProposalTier(file) === tier) return;
    try {
      await api.patch(`api/v1/rfp/knowledge-base/documents/${file.id}/evidence-tier/`, {
        evidence_tier: tier,
      });
      setTierOverrides((prev) => ({ ...prev, [file.id]: tier }));
      hotToast.success(`Moved to ${PROPOSAL_TIER_LABEL[tier]}.`);
    } catch (error) {
      console.error("Set proposal tier error:", error);
      hotToast.error("Couldn't update this document.");
    }
  };

  const handleDownload = async (file) => {
    if (!file.url) {
      showPopup("Error", "Download URL not available for this file.");
      return;
    }

    const fileName = file.name || `document.${file.file_type || "file"}`;
    try {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      // Could not fetch the file for a renamed download (e.g. CORS) —
      // fall back to opening it in a separate tab.
      const opened = window.open(file.url, "_blank", "noopener,noreferrer");
      if (!opened) showPopup("Error", "Failed to download file. Please try again.");
    }
  };

  const toggleActionMenu = (index, event) => {
    event.stopPropagation();
    if (isActionMenuOpen === index) {
      setIsActionMenuOpen(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setActionMenuPosition({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX - 20,
      });
      setIsActionMenuOpen(index);
    }
  };

  return (
    <div className="max-w-full overflow-x-hidden flex flex-col flex-grow font-outfit ">
      <div>
        <Header icon={"/arrow-back.svg"} name="Go to Knowledge Base" />
      </div>

      <div className="w-full px-3 mt-2">
        <div className="w-[99%] ml-1">
          {/* Header Controls Area - Aligned exactly with table */}
          <div className="flex flex-col gap-4 mb-2">
            
            {/* Top Row: Folder Title and Upload Button */}
            <div className="flex justify-between items-center w-full ">
              <div className="flex items-center gap-2">
                <img
                  src="/folder-icon-folder.svg"
                  alt="Folder Icon"
                  className="w-7 h-7 flex-shrink-0"
                />
                <h2 className="text-[#171717] font-outfit text-[17px] font-medium m-0 tracking-tight">
                  {folders.folder_name || "N/A"}
                </h2>
              </div>

              {selectedFiles.length === 0 && (
                <div className="relative inline-block">
                  <button
                    className="flex items-center justify-center gap-2 px-[22px] py-[9px] bg-[#C95EBE] rounded-[8px] text-white font-outfit text-[14px] font-medium hover:bg-[#b04b9e] transition-colors shadow-sm disabled:opacity-60"
                    onMouseEnter={() => setIsTooltipVisible(true)}
                    onMouseLeave={() => setIsTooltipVisible(false)}
                    onClick={() => fileInputRef.current.click()}
                    disabled={
                      subscriptionChecked &&
                      files.documents?.length > (isPaidYearlyPlan ? 10 : 5)
                    }
                  >
                    <img src="/upload_white.svg" alt="Upload" className="w-[18px] h-[18px]" />
                    Upload
                  </button>

                  {isTooltipVisible && (
                    <div className="absolute top-[115%] right-0 w-[178px] bg-white shadow-lg border border-gray-100 rounded-lg p-3 z-[1000] text-left transition-opacity duration-200">
                      <p className="text-[#171717] font-outfit text-[13px] font-medium leading-relaxed m-0">
                        Supported formats:<br />
                        <span className="text-gray-600 font-normal">Pdf, Txt, Csv, Xls, Docx<br />
                        Xlsx, Jpg, Jpeg, Png</span><br />
                        <span className="mt-1 block text-[#C95EBE]">Max Size : 25MB</span>
                      </p>
                    </div>
                  )}

                  <input
                    id="fileInput"
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    accept=".pdf,.xls,.xlsx,.csv,.txt,.doc,.jpeg,.jpg,.png"
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>

            {/* Bottom Row: Search, Filter, Bulk Actions */}
            <div className="flex justify-between items-center w-full ">
              <div className="flex items-center gap-3">
                {/* Search Box */}
                <div
                  className={`flex w-[380px] h-[40px] px-4 py-2 items-center gap-2 shrink-0 rounded-[24px] bg-white transition-all duration-200 overflow-hidden ${
                    isSearchFocused
                      ? "border border-[#BB42AE]/36 shadow-[0_11px_3px_transparent,0_7px_3px_rgba(0,0,0,0.01),0_4px_2px_rgba(0,0,0,0.05),0_2px_2px_rgba(0,0,0,0.09),0_0_1px_rgba(0,0,0,0.10)]"
                      : "border border-black/10"
                  }`}
                >
                  <img src="/search.svg" alt="Search" className="w-[18px] h-[18px] flex-shrink-0 opacity-70" />
                  <input
                    type="text"
                    placeholder="Search Documents Here"
                    className="font-outfit text-[14px] font-normal border-none outline-none w-full text-[#171717] bg-transparent whitespace-nowrap placeholder:text-gray-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                  />
                </div>

                {/* Filter Button */}
                <div className="relative group inline-block">
                  <button
                    className={`flex items-center justify-center gap-2 h-[40px] px-5 rounded-[24px] bg-white transition-all duration-200 ${
                      isFilterApplied
                        ? "border border-[#BB42AE]/36 shadow-sm"
                        : "border border-black/10"
                    }`}
                    onClick={toggleFilterModal}
                  >
                    <img
                      src="/filter-icon-first.svg"
                      alt="Filter Icon"
                      className="w-[16px] h-[16px] flex-shrink-0"
                    />
                    <span className="font-outfit text-[#171717] text-[14px] font-medium">Filter</span>
                  </button>

                  {isFilterApplied && (
                    <img
                      src="/Filter_CTA.svg"
                      alt="Clear Filter"
                      className="absolute -bottom-1 -right-1 w-[18px] h-[18px] cursor-pointer z-10 bg-white rounded-full shadow-sm hover:scale-110 transition-transform"
                      onClick={clearFilters}
                    />
                  )}

                  {isFilterApplied && (
                    <div className="absolute top-[115%] left-0 z-50 min-w-[150px] rounded-xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 px-4 py-3 text-sm text-[#171717] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                      <div className="font-semibold mb-2 text-[#171717]">Filters Applied</div>
                      <ul className="list-disc pl-4 space-y-1.5 text-[#52525B] font-normal text-[13px]">
                        {selectedFilters.normalDocument && <li>All Documents</li>}
                        {selectedFilters.folder && <li>Folders</li>}
                        {selectedFilters.contract && <li>Contracts</li>}
                        {selectedFilters.invoice && <li>Invoices</li>}
                        {selectedFilters.spreadsheet && <li>Spreadsheets</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Bulk Actions Array */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {showBulkActions && (
                  <>
                    <button
                      onClick={handleBulkExport}
                      disabled={
                        selectedFiles.length === 0 ||
                        !files.documents.some(
                          (file) =>
                            selectedFiles.includes(file.id) &&
                            (file.type === "contract" || file.type === "invoice")
                        )
                      }
                      className="flex items-center justify-center gap-1.5 px-[16px] py-[8px] rounded-full bg-[#F0F7FF] hover:bg-[#E0EFFF] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      <img
                        src="/export-icon-blue.svg"
                        alt="Export"
                        className="w-[14px] h-[14px] flex-shrink-0"
                      />
                      <span className="text-[#0066FF] font-outfit text-[14px] font-medium leading-none">
                        {isExporting ? "Exporting..." : "Export"}
                      </span>
                    </button>

                    <button
                      onClick={handleBulkDeleteClick}
                      disabled={selectedFiles.length === 0}
                      className="flex items-center justify-center gap-1.5 px-[16px] py-[8px] rounded-full bg-[#FFF0F0] hover:bg-[#FFE0E0] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      <img
                        src="/delete-icon-red.svg"
                        alt="Delete"
                        className="w-[14px] h-[14px] flex-shrink-0"
                      />
                      <span className="text-[#E21919] font-outfit text-[14px] font-medium leading-none">
                        Delete
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        setSelectedFiles([]);
                        setShowBulkActions(false);
                      }}
                      className="flex items-center justify-center p-[8px] rounded-full bg-[#F4F4F5] hover:bg-[#E4E4E7] transition-colors"
                    >
                      <img
                        src="/cancel_icon.svg"
                        alt="Cancel"
                        className="w-[14px] h-[14px] opacity-70"
                      />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* TABLE UI */}
          <div
            className="relative overflow-auto custom-scrollbar"
            style={{ height: "calc(100vh - 240px)" }}
          >
            <table className="w-full table-fixed text-sm text-left border-separate border-spacing-0">
              <thead className="bg-white h-12 text-[#1E1E1E] font-medium">
                <tr>
                  <th className="w-[5%] px-3 sticky top-0 bg-white z-10 border-b border-black/10 align-middle text-center">
                    <input
                      type="checkbox"
                      className="custom-checkbox mx-auto"
                      style={{
                        appearance: "none",
                        border: "1.5px solid #c0c0c0",
                        borderRadius: "4px",
                        cursor: "pointer",
                        width: "18px",
                        height: "18px",
                        backgroundImage:
                          files.documents?.length > 0 &&
                          selectedFiles?.length === files.documents.length
                            ? "url('/checck.svg')"
                            : "none",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "14px 14px",
                        display: "block",
                      }}
                      checked={
                        files.documents?.length > 0 &&
                        selectedFiles?.length === files.documents.length
                      }
                      onChange={handleSelectAll}
                      disabled={!files.documents?.length}
                    />
                  </th>

                  <th className="w-[38%] px-2 py-2 align-middle text-[#73607B] font-outfit text-[14px] font-semibold sticky top-0 bg-white z-10 border-b border-black/10">
                    <span className="block truncate">File Name</span>
                  </th>
                  
                  <th className="w-[15%] px-3 py-2 align-middle text-[#73607B] font-outfit text-[14px] font-semibold sticky top-0 bg-white z-10 border-b border-black/10 text-center">
                    <span className="block truncate">File Type</span>
                  </th>

                  <th className="w-[12%] px-3 py-2 align-middle text-[#73607B] font-outfit text-[14px] font-semibold sticky top-0 bg-white z-10 border-b border-black/10">
                    <div
                      className="flex items-center gap-1.5 cursor-pointer justify-center"
                      onClick={handleSortBySize}
                    >
                      <span className="truncate">Size</span>
                      <img
                        src="/icons-size-icon.svg"
                        alt="Size Icon"
                        className="w-3.5 h-3.5 flex-shrink-0"
                      />
                    </div>
                  </th>
                  
                  <th className="w-[18%] px-3 py-2 align-middle text-[#73607B] font-outfit text-[14px] font-semibold sticky top-0 bg-white z-10 border-b border-black/10">
                    <div
                      className="flex items-center gap-1.5 cursor-pointer justify-center"
                      onClick={handleSortByDate}
                    >
                      <span className="truncate">Uploaded Date</span>
                      <img
                        src="/icons-size-icon.svg"
                        alt="Date Icon"
                        className="w-3.5 h-3.5 flex-shrink-0"
                      />
                    </div>
                  </th>

                  <th className="w-[12%] px-3 py-2 align-middle text-center text-[#73607B] font-outfit text-[14px] font-semibold sticky top-0 bg-white z-10 border-b border-black/10">
                    <span className="block truncate">Actions</span>
                  </th>
                </tr>
              </thead>
              
              <tbody className="bg-white">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="py-20 border-b border-black/10 bg-white">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C95EBE] mx-auto mb-4"></div>
                        <p className="text-[#52525B] font-outfit text-[15px]">
                          Loading Files...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : files.documents && files.documents.length > 0 ? (
                  paginatedDocuments.map((file, index) => {
                    const isProposalsFolder = folders.folder_name === "Previous Proposals";
                    const prevFile = paginatedDocuments[index - 1];
                    const showGroupHeader =
                      isProposalsFolder &&
                      (index === 0 ||
                        proposalTierRank(file, tierOverrides) !== proposalTierRank(prevFile, tierOverrides));

                    return (
                    <React.Fragment key={file.id}>
                    {showGroupHeader && (
                      <tr>
                        <td colSpan="6" className="px-3 pt-3 pb-1.5 bg-white border-b border-black/5">
                          <span className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[#A1A1AA]">
                            {proposalTierLabel(file, tierOverrides)}
                          </span>
                        </td>
                      </tr>
                    )}
                    <tr
                      className="group cursor-pointer hover:bg-[#F8F3FF] transition-colors"
                    >
                      <td className="w-[5%] px-3 py-2 align-middle text-center border-b border-black/10">
                        <input
                          type="checkbox"
                          className="custom-checkbox mx-auto"
                          style={{
                            appearance: "none",
                            border: "1.5px solid #c0c0c0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            width: "18px",
                            height: "18px",
                            backgroundImage: selectedFiles.includes(file.id)
                              ? "url('/checck.svg')"
                              : "none",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "14px 14px",
                            display: "block",
                          }}
                          checked={selectedFiles.includes(file.id)}
                          onChange={() => handleCheckboxChange(file.id)}
                        />
                      </td>

                      <td className="w-[38%] px-2 py-2 align-middle text-[#171717] font-outfit text-[14px] font-medium border-b border-black/10">
                        <div className="flex items-center gap-3">
                          <img
                            src={
                              file.file_type === "pdf"
                                ? "/pdff.svg"
                                : file.file_type === "xlsx"
                                ? "/Xls.svg"
                                : file.file_type === "docx"
                                ? "/docX.svg"
                                : file.file_type === "csv"
                                ? "/CSV.svg"
                                : file.file_type === "txt"
                                ? "/TXT.svg"
                                : file.file_type === "xls"
                                ? "/Xls.svg"
                                : "/pdf.svg"
                            }
                            className="w-[22px] h-[22px] flex-shrink-0"
                            alt="Icon"
                          />
                          <span className="truncate" title={file.name || "N/A"}>
                            {file.name || "N/A"}
                          </span>
                        </div>
                      </td>

                      <td className="w-[15%] px-3 py-2 align-middle text-[#52525B] font-outfit text-[14px] font-medium border-b border-black/10 text-center">
                        <span className="capitalize">{file.type}</span>
                      </td>
                      
                      <td className="w-[12%] px-3 py-2 align-middle text-[#52525B] font-outfit text-[14px] font-medium border-b border-black/10 text-center">
                        <span>{file.size}</span>
                      </td>
                      
                      <td className="w-[18%] px-3 py-2 align-middle text-[#52525B] font-outfit text-[14px] font-medium border-b border-black/10 text-center">
                        <span>{file.uploaded_at}</span>
                      </td>

                      <td className="w-[12%] px-3 py-2 align-middle text-center border-b border-black/10">
                        <div 
                          className="flex items-center justify-center w-8 h-8 mx-auto rounded-full hover:bg-gray-100 transition-colors"
                          onClick={(e) => toggleActionMenu(index, e)}
                        >
                          <img
                            src="/action.svg"
                            alt="Actions"
                            className="w-[20px] h-[20px] cursor-pointer"
                          />
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="py-16 text-center bg-white border-b border-black/10">
                      <div className="flex flex-col items-center justify-center">
                        <img
                          src="/no-contracts.svg"
                          alt="No Documents Found"
                          className="mb-4 w-[120px] h-[120px]"
                        />
                        <p className="text-[#52525B] font-outfit text-[15px]">
                          Looks like your Table is empty!
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <UploadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        filesToUpload={filesToUpload}
        folderId={folderId}
        onUploadSuccess={handleUploadSuccess}
        uploadEndpoint={`api/v1/readpdf/upload-in-folder/${folderId}/`}  
      />
      
      {isPopupVisible && (
        <div className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-[12px] shadow-xl w-[440px] p-6 text-center animate-[fadeIn_0.2s_ease-out]">
            <h2 className="text-[#171717] font-outfit text-[18px] font-semibold mb-3">
              Oops! Maximum Limit Reached
            </h2>
            <p className="text-[#52525B] font-outfit text-[14px] leading-relaxed mb-6">
              This folder allows a maximum of {isPaidYearlyPlan ? 10 : 5}{" "}
              documents. To upload more, please delete an existing document.
            </p>
            <button
              className="bg-[#C95EBE] hover:bg-[#b04b9e] text-white font-outfit text-[14px] font-medium px-6 py-2.5 rounded-[8px] transition-colors"
              onClick={() => setIsPopupVisible(false)}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {isDeleting && (
        <div className="fixed bottom-6 right-6 bg-[#171717] text-white font-outfit text-[14px] px-5 py-3 rounded-[8px] shadow-lg z-[10000] flex items-center gap-3 animate-[slideUp_0.3s_ease-out]">
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          Deleting documents...
        </div>
      )}

      <FilterModalForFolder
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={(filteredFiles, filteredFolders) => {
          setFiles(filteredFiles);
          setFolders(filteredFolders);
          setIsFilterApplied(true);
        }}
        setFiles={setFiles}
        setFolders={setFolders}
        setIsFilterApplied={setIsFilterApplied}
        folderId={folderId}
        src="folder"
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        clearFilters={clearFilters}
      />

      <RenameModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        onRename={handleRename}
        fileName={currentFile?.name || ""}
        loading={isRenamingDoc}
      />

      <DeleteModal
        isOpen={confirmDeleteVisible}
        onClose={() => setConfirmDeleteVisible(false)}
        onDelete={confirmDelete}
        fileName={
          isBulkDelete
            ? `${selectedFiles.length} selected document(s)`
            : contractToDelete?.name || ""
        }
        loading={deleteInProgress}
        deleteSuccess={deleteSuccess}
      />

      <RenameModal
        isOpen={isRenameFolderModalOpen}
        onClose={() => setIsRenameFolderModalOpen(false)}
        onRename={handleRenameFolder}
        fileName={renameFolderInput}
        loading={isRenamingFolder}
      />

      {isActionMenuOpen !== null && (
        <div
          className="fixed z-[9999] bg-white rounded-[12px] shadow-[0_4px_20px_rgba(0,0,0,0.12)] border border-gray-100 p-1.5 min-w-[150px] font-outfit"
          ref={actionMenuRef}
          style={{
            top: isActionMenuOpen >= paginatedDocuments.length - 2
                 ? `${actionMenuPosition.top - (folders.folder_name === "Previous Proposals" ? 260 : 140)}px`
                 : `${actionMenuPosition.top}px`,
            left: `${actionMenuPosition.left - 130}px`,
          }}
        >
          {/* Move between Submitted / Won / Work Completed — only inside
              "Previous Proposals", the one folder every captured proposal
              sits in regardless of outcome. */}
          {folders.folder_name === "Previous Proposals" && (
            <>
              <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#A1A1AA]">
                Outcome
              </div>
              {PROPOSAL_TIERS.map((t) => {
                const file = paginatedDocuments[isActionMenuOpen] || {};
                const active = currentProposalTier(file) === t.key;
                const hoverKey = `tier-${t.key}`;
                return (
                  <div
                    key={t.key}
                    onMouseEnter={() => setHoveredAction(hoverKey)}
                    onMouseLeave={() => setHoveredAction(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetProposalTier(file, t.key);
                      setIsActionMenuOpen(null);
                    }}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-[8px] cursor-pointer transition-colors mt-0.5 ${
                      active
                        ? "text-[#C95EBE]"
                        : hoveredAction === hoverKey
                          ? "bg-[#FAF9F6] text-[#C95EBE]"
                          : "text-[#52525B]"
                    }`}
                  >
                    <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                      {active && (
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span className="text-[14px] font-medium">{t.label}</span>
                  </div>
                );
              })}
              <div className="my-1 border-t border-black/5" />
            </>
          )}

          {/* Rename */}
          <div
            onMouseEnter={() => setHoveredAction("rename")}
            onMouseLeave={() => setHoveredAction(null)}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentFile(paginatedDocuments[isActionMenuOpen]);
              setIsRenameModalOpen(true);
              setIsActionMenuOpen(null);
            }}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-[8px] cursor-pointer transition-colors ${
              hoveredAction === "rename"
                ? "bg-[#FAF9F6] text-[#C95EBE]"
                : "text-[#52525B]"
            }`}
          >
            <img
              src={
                hoveredAction === "rename"
                  ? "/edit-active-contract.svg"
                  : "/edit.svg"
              }
              alt="Edit"
              className="w-4 h-4"
            />
            <span className="text-[14px] font-medium">Rename</span>
          </div>

          {/* Download */}
          <div
            onMouseEnter={() => setHoveredAction("download")}
            onMouseLeave={() => setHoveredAction(null)}
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(paginatedDocuments[isActionMenuOpen]);
              setIsActionMenuOpen(null);
            }}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-[8px] cursor-pointer transition-colors mt-0.5 ${
              hoveredAction === "download"
                ? "bg-[#FAF9F6] text-[#C95EBE]"
                : "text-[#52525B]"
            }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                color: hoveredAction === "download" ? "#C95EBE" : "currentColor",
              }}
            >
              <path
                d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.66675 6.66667L8.00008 10L11.3334 6.66667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 10V2"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-[14px] font-medium">Download</span>
          </div>

          {/* Delete */}
          <div
            onMouseEnter={() => setHoveredAction("delete")}
            onMouseLeave={() => setHoveredAction(null)}
            onClick={(e) => {
              e.stopPropagation();
              handleIndividualDelete(paginatedDocuments[isActionMenuOpen]);
              setIsActionMenuOpen(null);
            }}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-[8px] cursor-pointer transition-colors mt-0.5 ${
              hoveredAction === "delete"
                ? "bg-[#FFF0F0] text-[#E21919]"
                : "text-[#52525B]"
            }`}
          >
            <img
              src={
                hoveredAction === "delete"
                  ? "/delete-active-contract.svg"
                  : "/delete.svg"
              }
              alt="Delete"
              className="w-4 h-4"
            />
            <span className="text-[14px] font-medium">Delete</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default KbFolderPage;
